import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { interactionsAPI } from '../api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Phone, 
  Mail, 
  AlertTriangle, 
  Search, 
  Filter,
  User,
  Building2,
  Truck,
  Package,
  Clock,
  ChevronRight
} from 'lucide-react';

const Interactions = () => {
  const navigate = useNavigate();
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [interactionTypeFilter, setInteractionTypeFilter] = useState('all');


  const searchRef = useRef(search);
  searchRef.current = search;

  const fetchInteractions = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (entityTypeFilter !== 'all') filters.entityType = entityTypeFilter;
      if (interactionTypeFilter !== 'all') filters.interactionType = interactionTypeFilter;
      if (searchRef.current) filters.search = searchRef.current;
      filters.limit = 100;
      
      const res = await interactionsAPI.getAll(filters);
      setInteractions(res.data);
    } catch (error) {
      toast.error('Failed to load interactions');
    } finally {
      setLoading(false);
    }
  }, [entityTypeFilter, interactionTypeFilter]);

  useEffect(() => {
    fetchInteractions();
  }, [fetchInteractions]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInteractions();
  };

  const getInteractionIcon = (type) => {
    switch (type) {
      case 'call': return <Phone className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'escalation': return <AlertTriangle className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case 'customer': return <Building2 className="w-4 h-4" />;
      case 'carrier': return <Truck className="w-4 h-4" />;
      case 'load': return <Package className="w-4 h-4" />;
      default: return <Building2 className="w-4 h-4" />;
    }
  };

  const getInteractionTypeColor = (type) => {
    switch (type) {
      case 'call': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'email': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'escalation': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const navigateToEntity = (entityType, entityId) => {
    switch (entityType) {
      case 'customer': navigate(`/customers/${entityId}`); break;
      case 'carrier': navigate(`/carriers/${entityId}`); break;
      case 'load': navigate(`/loads/${entityId}`); break;
      case 'facility': navigate(`/facilities/${entityId}`); break;
      default: break;
    }
  };

  return (
    <MainLayout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="interactions-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[var(--tms-text)]">Notes & Interactions</h1>
          <p className="text-sm text-[var(--tms-text-muted)] mt-1">View all notes and interactions across your system</p>
        </div>

        {/* Filters */}
        <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-xl p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--tms-text-muted)]" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search notes, subjects, content..."
                  className="pl-10"
                  data-testid="search-interactions"
                />
              </div>
              <Button type="submit" variant="outline" size="sm">
                <Filter className="w-4 h-4 mr-1" />
                Search
              </Button>
            </form>
            
            <div className="flex gap-2">
              <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
                <SelectTrigger className="w-[130px]" data-testid="filter-entity-type">
                  <SelectValue placeholder="Entity Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="customer">Customers</SelectItem>
                  <SelectItem value="carrier">Carriers</SelectItem>
                  <SelectItem value="load">Loads</SelectItem>
                  <SelectItem value="facility">Facilities</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={interactionTypeFilter} onValueChange={setInteractionTypeFilter}>
                <SelectTrigger className="w-[130px]" data-testid="filter-interaction-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="note">Notes</SelectItem>
                  <SelectItem value="call">Calls</SelectItem>
                  <SelectItem value="email">Emails</SelectItem>
                  <SelectItem value="escalation">Escalations</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Interactions List */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-12 text-[var(--tms-text-muted)]">
              Loading interactions...
            </div>
          ) : interactions.length === 0 ? (
            <div className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-xl p-8 text-center">
              <MessageSquare className="w-12 h-12 mx-auto text-[var(--tms-text-muted)] mb-3" />
              <h3 className="text-lg font-medium text-[var(--tms-text)] mb-1">No interactions found</h3>
              <p className="text-sm text-[var(--tms-text-muted)]">
                Add notes to customers, carriers, or loads to see them here.
              </p>
            </div>
          ) : (
            interactions.map((interaction) => (
              <div
                key={interaction.id}
                className="bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-xl p-4 hover:border-[var(--tms-primary)]/50 transition-all cursor-pointer"
                onClick={() => navigateToEntity(interaction.entity_type, interaction.entity_id)}
                data-testid={`interaction-${interaction.id}`}
              >
                <div className="flex items-start gap-3">
                  {/* Interaction Type Icon */}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${getInteractionTypeColor(interaction.interaction_type)}`}>
                    {getInteractionIcon(interaction.interaction_type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    {/* Header Row */}
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-semibold text-[var(--tms-text)] truncate">
                          {interaction.subject}
                        </span>
                        <span className={`px-2 py-0.5 text-xs rounded-full capitalize ${getInteractionTypeColor(interaction.interaction_type)}`}>
                          {interaction.interaction_type}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-[var(--tms-text-muted)] flex-shrink-0">
                        <Clock className="w-3 h-3" />
                        {formatDate(interaction.created_at)}
                      </div>
                    </div>
                    
                    {/* Content Preview */}
                    <p className="text-sm text-[var(--tms-text-secondary)] line-clamp-2 mb-2">
                      {interaction.content}
                    </p>
                    
                    {/* Footer Row */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-xs text-[var(--tms-text-muted)]">
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {interaction.created_by_name}
                        </div>
                        <div className="flex items-center gap-1">
                          {getEntityIcon(interaction.entity_type)}
                          <span className="capitalize">{interaction.entity_type}:</span>
                          <span className="text-[var(--tms-primary)]">{interaction.entity_name}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[var(--tms-text-muted)]" />
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Interactions;
